/**
 * useTenantSocket — subscribe to the per-tenant WebSocket event bus.
 *
 * Mounts one connection to `/api/events/ws?token=<jwt>`, hands every
 * incoming JSON message to the caller's `onMessage` callback, and cleans
 * up on unmount (timer + socket close).
 *
 * Auto-reconnects with exponential backoff up to 30s. Silent on errors —
 * any consumer that needs to know the socket dropped can read the
 * returned `status` ref.
 *
 * Used by: FinanceEngine.jsx + TenantSettings.jsx (live reporting-currency
 * updates), Events.jsx (room bookings — inline impl, predates this hook).
 */
import { useEffect, useRef } from 'react';

export default function useTenantSocket(onMessage, { enabled = true } = {}) {
  const statusRef = useRef('idle'); // 'idle' | 'connecting' | 'open' | 'closed'
  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const onMessageRef = useRef(onMessage);
  // Keep the latest callback in a ref so the effect doesn't re-tear-down the
  // socket every time the parent re-renders with a fresh inline function.
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
      if (!base) return;
      statusRef.current = 'connecting';
      let ws;
      try {
        // Cookie-first auth: browsers send the HttpOnly session_token cookie
        // automatically on same-origin WS handshakes. The query param
        // approach is kept off-by-default to avoid leaking the JWT in
        // server logs / referrer headers.
        ws = new WebSocket(`${base}/api/events/ws`);
      } catch {
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        statusRef.current = 'open';
        retryDelayRef.current = 1000;
        // 25s keepalive — server expects an occasional "ping"
        pingTimerRef.current = setInterval(() => {
          try { ws.send('ping'); } catch { /* noop */ }
        }, 25000);
      };
      ws.onmessage = (evt) => {
        let payload;
        try { payload = JSON.parse(evt.data); } catch { return; }
        try { onMessageRef.current?.(payload); } catch { /* swallow consumer errors */ }
      };
      ws.onerror = () => { /* will retry via onclose */ };
      ws.onclose = () => {
        statusRef.current = 'closed';
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
        if (cancelled) return;
        // Exponential backoff retry (1s, 2s, 4s, 8s, 16s, capped at 30s)
        const delay = Math.min(30000, retryDelayRef.current);
        retryDelayRef.current = delay * 2;
        retryTimerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
      statusRef.current = 'idle';
    };
  }, [enabled]);

  return statusRef;
}
